// =============================================================================
// configurator-2026-carousel.js
// -----------------------------------------------------------------------------
// Step-by-step carousel shell for the Anthros configurator.
//
// ARCHITECTURE: This file adds ZERO pricing, SKU, or 3D logic. The real
// MageWorx APO inputs stay in the DOM (moved off-screen by the companion CSS).
// This carousel is a presentational PROXY layer: each option button forwards a
// real .click() to the underlying APO radio (or sets the APO <select>). APO then
// recomputes price + SKU exactly as it does today, the existing Dopple bridge
// (handleOptionsReady, listening on `input`) updates the 3D model for free, and
// the existing add-to-cart / snapshot / Klaviyo pipeline fires unchanged because
// the review CTA proxies a click to `.mw-apo-add-to-cart-cloned`.
//
// We never compute price here. We READ it from `.price-item--regular`, which APO
// keeps current. That is the single source of truth, so the header and the
// review total can never disagree with checkout.
//
// On each step entry we re-mirror APO's CURRENT visible options. That is what
// makes the Quartz cascade, OOS hiding, and backward-edit invalidation "just
// work": we always render APO's present truth instead of a cached list.
// =============================================================================

(function () {
  'use strict';

  // ---- Step definitions (carousel order = 1..9, then review = 10) -----------
  // section = the APO wrapper class (.chair_summary.o_#######)
  // info    = optional popover text (string, or fn(activeTabId) -> string)
  // tabs    = optional sub-type tabs; subtypeOf(valueEl) buckets each option
  const STEPS = [
    { key: 'color', label: 'Chair Color', section: 'o_2184550',
      subhead: 'Choose your chair color from the options below.'
    },
    {
      key: 'fabric', label: 'Fabric', section: 'o_2184551',
      subhead: 'Choose your fabric from the options below.',
      tabs: [
        { id: 'athlon', label: 'Athlon\nPerformance Fabric' },
        { id: 'port', label: 'Port\nLeather Alternative' }
      ],
      subtypeOf: function (el) {
        if (el.closest('.port_leather')) return 'port';
        if (el.closest('.shapeknit')) return 'athlon';
        return 'athlon';
      },
      info: function (tab) {
        if (tab === 'port') {
          return 'An environmentally conscious synthetic leather that is phthalate free. Wipeable for cleaning and disinfecting.\n\nEven with our high-performance materials, lighter upholstery may show discoloration or dye transfer over time. You might consider a darker color option if this concerns you.';
        }
        return 'A high performance upholstery designed to meet the demands of daily use while providing exceptional comfort and style.\n\nEven with our high-performance fabrics, lighter textiles may show discoloration or dye transfer over time. You might consider a darker color option if this concerns you.';
      }
    },
    {
      key: 'back', label: 'Back Panel', section: 'o_2184552',
      camera: 'back',
      subhead: 'Choose your back panel from the options below.',
      tabs: [
        { id: 'standard', label: 'Standard' },
        { id: 'wood', label: 'Wood Grain' },
        { id: 'specialty', label: 'Specialty' }
      ],
      subtypeOf: function (el) {
        if (el.closest('.standard_black')) return 'standard';
        if (el.closest('.wood_section')) return 'wood';
        if (el.closest('.specialty') || el.closest('.bitcoin_data')) return 'specialty';
        return 'standard';
      },
      // popover ONLY on Wood Grain (Eric + Stacy copy). Standard + Specialty have
      // no popover; the subhead above the tabs carries the guidance instead.
      info: function (tab) {
        if (tab === 'wood') return 'Crafted with a refined wood aesthetic that captures the organic beauty and texture of natural wood grain.';
        return null;
      }
    },
    { key: 'caps', label: 'Control Knob Caps', section: 'o_2184553',
      subhead: 'Choose your control knob caps from the options below.',
      // stack: SHINY on line 1, colour on line 2
      labelStack: function (label) {
        const up = (label || '').toUpperCase().replace(/\s+/g, ' ').trim();
        const m = up.match(/^SHINY\s+(.+)$/);
        if (m) return ['SHINY', m[1]];
        return null;
      }
    },
    {
      key: 'base', label: 'Base', section: 'o_2184554',
      camera: 'back',
      subhead: 'Choose your base style from the options below.',
      // per-option popovers (each has its own video at the top, copy below).
      // PLACEHOLDER copy + media below -- fill in when the assets are ready.
      infoByOption: function (label) {
        const up = (label || '').toUpperCase();
        if (up.indexOf('FIXED') > -1 || up.indexOf('4') > -1) {
          return '4 Spoke Base\n\nDOES NOT SWIVEL, MOVES WITH CHAIR - Protects feet from hitting casters. Works great for hard surfaces.';
        }
        return '5 Spoke Base\n\nStandard swivel base. Recommended for ALL surfaces. Recommended for L-shaped desks.';
      },
      // video shown at the TOP of the popover, per option
      mediaByOption: function (label) {
        const up = (label || '').toUpperCase();
        if (up.indexOf('FIXED') > -1 || up.indexOf('4') > -1) {
          return 'https://cdn.shopify.com/videos/c/o/v/058c93b6e6b94bf99f9687f126ee6833.mp4';
        }
        return 'https://cdn.shopify.com/videos/c/o/v/775f9b23c8cd4877807e9fa71c2f421d.mp4';
      },
      // feature word (SWIVEL / FIXED) leads, spoke count below
      labelStack: function (label) {
        const up = (label || '').toUpperCase();
        if (up.indexOf('SWIVEL') > -1) return ['SWIVEL', '5 SPOKE'];
        if (up.indexOf('FIXED') > -1) return ['FIXED', '4 SPOKE'];
        return null;
      }
    },
    { key: 'casters', label: 'Casters', section: 'o_2294855', preferSelect: true, hideWhenSingle: true,
      subhead: 'Choose your casters from the options below.'
    },
    {
      key: 'arms', label: 'Arms', section: 'o_2184557',
      subhead: 'Choose your arms from the options below.',
      // popover is JUST for 4D Arms; ends after "pivot." per Sam
      infoByOption: function (label) {
        const up = (label || '').toUpperCase();
        if (up.indexOf('4D') > -1) return '4D arms adjust height, width, depth and pivot.';
        return null;
      }
    },
    {
      key: 'foot', label: 'Foot Support', section: 'o_2206346',
      subhead: 'Recommended for height below 5\'8" OR desk height is over 25',
      info: 'Your seat height should be set to match your desk—not the floor. If raising your seat or using tilt leaves your feet without firm support, we recommend a footrest.',
      media: 'https://cdn.shopify.com/videos/c/o/v/791c84acf2b343f591f65f6961b95fce.webm',
      // stack FOOT / SUPPORT so "No Foot Support" lines up evenly
      labelStack: function (label) {
        const up = (label || '').toUpperCase().replace(/\s+/g, ' ').trim();
        if (up === 'FOOT SUPPORT') return ['FOOT', 'SUPPORT'];
        if (up.indexOf('NO FOOT') > -1) return ['NO FOOT', 'SUPPORT'];
        return null;
      }
    },
    {
      key: 'tilt', label: 'Tilt Optimization', section: 'o_2406167',
      subhead: 'Choose your tilt optimization from the options below.',
      info: 'Choosing the right spring ensures the chair responds correctly to your body: too stiff and it won\'t tilt with you; too light and it won\'t support you in tilt.\n\nStandard  150 lbs to 300 lbs\nLight  Under 150 lbs (tested up to 160 lbs)\n\n*Overall 300 lb weight capacity'
    }
  ];

  const REVIEW_INDEX = STEPS.length;          // step 10
  const TOTAL_STEPS = STEPS.length + 1;       // 9 config + review

  // clean inline cart glyph (matches the standardized Add To Cart icon)
  const CART_SVG = '<svg class="cfg2026-cart-svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>';

  // ---- Sale ------------------------------------------------------------------
  // Nothing about the promo is decided here. `window.ANTHROS_SALE` (set in
  // the-anthros-chair-2026.js) owns the flag, the deadline and the
  // original-price map; this file only renders it. Read lazily so script order
  // never matters.
  function sale() { return window.ANTHROS_SALE || { active: false }; }
  function saleOriginal(id) {
    const s = sale();
    return (s.active && id && s.originalPrices) ? (s.originalPrices[id] || 0) : 0;
  }
  function saleSavings() {
    const s = sale();
    return (s.active && typeof s.getSavings === 'function') ? s.getSavings() : 0;
  }
  function money(n) { return '$' + Math.round(n).toLocaleString('en-US'); }

  // The carousel is the MOBILE experience only. At 1025px and up the page keeps
  // the older desktop design, so nothing here initializes and none of the
  // .cfg2026-* CSS applies (it is all gated behind body.cfg2026-active, which
  // only gets added below). Keep this in sync with the media queries in
  // configurator-carousel-2026.css.
  const CFG_MAX_WIDTH = 1024;
  function isMobile() { return window.innerWidth <= CFG_MAX_WIDTH; }

  // ---- State ----------------------------------------------------------------
  let initialized = false;
  let current = 0;
  const activeTab = {};   // stepKey -> tab id
  const visited = {};     // stepKey -> true once the user picks an option on that step
  let root, panelBody, titleEl, countEl, infoBtn, infoPop, infoPopX, infoPopBody, stage, collapsed = false;
  let selCard, selList, selCount, selectionsOpen = false;
  let floatAffirm, floatBlack, progressFill;
  let saleBanner, saveChip, countdownEl;
  let selAffirm;   // Affirm mirror inside the selections card (mobile checkout surface)
  let MODE = 'mobile';   // 'mobile' (carousel) | 'desktop' (stacked cards)
  let deskHost = null;   // desktop stacked-cards container
  let deskPriceEl = null, deskAffirmEl = null;   // desktop price bar nodes
  let deskPop = null, deskPopBody = null;   // desktop info popover (like mobile)

  // ---- Tiny DOM helpers -----------------------------------------------------
  const qs = function (s, r) { return (r || document).querySelector(s); };
  const qsa = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  const el = function (tag, cls, txt) {
    const n = document.createElement(tag);
    // A <button> with no type defaults to type="submit". The desktop cards are
    // inserted inside the theme's add-to-cart <form>, so an untyped button
    // there submits the form (= adds a base chair) on every option click.
    if (tag === 'button') n.type = 'button';
    if (cls) n.className = cls;
    if (txt != null) n.textContent = txt;
    return n;
  };
  function sectionEl(step) {
    return qs('.mw-apo-container .' + step.section) || qs('.' + step.section);
  }
  // Visible = laid out. APO-hidden options (display:none, e.g. Quartz cascade)
  // return zero client rects and are excluded. OOS options stay visible.
  function isVisible(node) { return !!node && node.getClientRects().length > 0; }
  function cleanName(t) { return t ? t.split('+')[0].replace(/\s+$/, '').trim() : ''; }
  function priceChip(dataPrice) {
    const n = parseFloat(dataPrice);
    return (isFinite(n) && n > 0) ? '+$' + Math.round(n) : '';
  }
  // During the sale APO charges $0 for the included upgrades, so the chip shows
  // the $0 the customer actually pays with the normal upcharge struck beneath.
  function appendChip(btn, o) {
    const orig = saleOriginal(o.id);
    if (orig) {
      btn.appendChild(el('span', 'cfg2026-chip cfg2026-chip--free', '$0'));
      btn.appendChild(el('s', 'cfg2026-chip-was', '+$' + orig));
      return;
    }
    const chip = priceChip(o.price);
    if (chip) btn.appendChild(el('span', 'cfg2026-chip', chip));
  }

  // ---- Read APO truth -------------------------------------------------------
  function getControl(step) {
    const sec = sectionEl(step);
    if (!sec) return { kind: 'none' };
    const sel = step.preferSelect ? sec.querySelector('select') : sec.querySelector('select');
    if (sel) return { kind: 'select', sec: sec, sel: sel };
    if (sec.querySelector('input[type="radio"]')) return { kind: 'radio', sec: sec };
    return { kind: 'none', sec: sec };
  }

  function radioOptions(step, sec) {
    return qsa('.mw-option__value[data-value-id]', sec).map(function (valEl) {
      const input = valEl.querySelector('input[type="radio"]');
      if (!input) return null;
      const imgNode = valEl.querySelector('.mw-option__swatch-media img, img.mw-option__swatch-image');
      const raw = (valEl.querySelector('.title_name') || {}).textContent || '';
      return {
        input: input,
        valueEl: valEl,
        id: input.id,                    // v_####### -- key into the sale price map
        label: cleanName(raw) || cleanName(input.value),
        price: input.getAttribute('data-price'),
        img: imgNode ? imgNode.src : '',
        checked: input.checked,
        oos: valEl.classList.contains('oos-item'),
        subtype: step.subtypeOf ? step.subtypeOf(valEl) : null
      };
    }).filter(Boolean).filter(function (o) { return isVisible(o.valueEl); });
  }

  function selectOptions(sel) {
    return qsa('option', sel).filter(function (o) {
      return o.value && o.value !== '' && o.value.toLowerCase() !== 'undefined'
        && !o.disabled && !o.hidden && !o.classList.contains('mw-hidden');
    }).map(function (o) {
      return { value: o.value, label: o.textContent.trim(), price: o.getAttribute('data-price'), selected: o.selected };
    });
  }

  // ---- Apply a choice (forward to the real APO control) ---------------------
  function chooseRadio(opt) {
    if (opt.oos) return;                 // out of stock: ignore (none today)
    if (!opt.input.checked) opt.input.click();   // native click -> APO + Dopple + price
    afterChange();
  }
  function chooseSelect(sel, value) {
    if (sel.value !== value) {
      sel.value = value;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    afterChange();
  }
  function afterChange() {
    stopAutoRotate();   // user engaged with an option: stop the attract loop
    // APO recomputes asynchronously; refresh price + current step shortly after.
    setTimeout(function () {
      if (MODE === 'desktop') { renderDesktopAll(); return; }
      updatePrice();
      if (current < REVIEW_INDEX) renderStep();
      if (selectionsOpen) renderSelections();   // keep the open card current
    }, 200);
  }

  // A step flagged hideWhenSingle disappears when APO leaves it only one
  // selectable option (e.g. Casters on Onyx, where Soft Surface is hidden).
  function isStepHidden(step) {
    if (!step.hideWhenSingle) return false;
    const ctrl = getControl(step);
    if (ctrl.kind === 'select') return selectOptions(ctrl.sel).length <= 1;
    if (ctrl.kind === 'radio') return radioOptions(step, ctrl.sec).length <= 1;
    return false;
  }

  // Guarantee the current step has a valid, visible selection. Handles the
  // Quartz cascade case where a previously-checked option got hidden.
  function ensureValidSelection(step) {
    const ctrl = getControl(step);
    if (ctrl.kind === 'radio') {
      const opts = radioOptions(step, ctrl.sec);
      if (opts.length && !opts.some(function (o) { return o.checked; })) {
        const first = opts.filter(function (o) { return !o.oos; })[0];
        if (first && !first.input.checked) first.input.click();
      }
    } else if (ctrl.kind === 'select') {
      if (!ctrl.sel.value) {
        const opts = selectOptions(ctrl.sel);
        if (opts.length) { ctrl.sel.value = opts[0].value; ctrl.sel.dispatchEvent(new Event('change', { bubbles: true })); }
      }
    }
  }

  // ---- Price (read-only mirror of APO) --------------------------------------
  // APO owns the live total. The theme's .price-item nodes are Shopify's static
  // product/compare-at pair: they never reflect option pricing, and which of
  // them holds which value isn't even stable. Read APO's element and keep the
  // theme selector only as a fallback for when APO isn't on the page.
  function readPrice() {
    const apo = qs('.mw-apo-price-custom .mw-text--style-strong');
    if (apo) {
      const t = (apo.textContent || '').trim();
      if (t) return t;
    }
    let txt = '';
    qsa('.price__regular .price-item--regular').some(function (n) {
      const t = (n.textContent || '').trim();
      if (t) { txt = t; return true; }
      return false;
    });
    return txt || '';
  }

  // APO repaints its total asynchronously after a radio changes, so reading it
  // immediately after the proxied click can catch the previous value. Watch the
  // node rather than guessing at a delay.
  function initPriceObserver() {
    const apo = qs('.mw-apo-price-custom');
    if (!apo || !('MutationObserver' in window)) return;
    new MutationObserver(function () { updatePrice(); })
      .observe(apo, { childList: true, subtree: true, characterData: true });
  }
  function updatePrice() {
    const p = readPrice();
    qsa('.cfg2026-price').forEach(function (n) { n.textContent = p; });
    updateSaleStrike(p);
    populateFloatExtras();
  }

  // The strike is what THIS build would cost at normal option pricing: the live
  // APO price (what the customer is charged) plus the upcharges the sale waives.
  // No selected upgrades means no savings, so the strike hides rather than
  // showing a number equal to the price.
  function updateSaleStrike(p) {
    if (!sale().active) return;
    const savings = saleSavings();
    const live = parseFloat((p || '').replace(/[$,]/g, ''));
    const txt = (savings > 0 && isFinite(live)) ? money(live + savings) : '';
    qsa('.cfg2026-price-was').forEach(function (n) {
      n.textContent = txt;
      n.style.display = txt ? '' : 'none';
    });
  }

  // Mirror the live Affirm line (text + logo img) and the Anthros Black upsell
  // link into the float-price block. Read from the real off-screen DOM so the
  // monthly figure, the Black price, and the href stay accurate. Hidden until a
  // source is found, so nothing shows if the elements aren't present.
  function populateFloatExtras() {
    // Only the Affirm line is mirrored from the live DOM (its monthly figure
    // tracks price). The Anthros Black link is hardcoded at build time (text +
    // clean href), so it is intentionally NOT touched here -- reading it from the
    // page would replace our copy with the product image and the variant URL.
    const realAffirm = firstRealMatch('.affirm-line');
    if (!realAffirm) return;
    mirrorAffirm(floatAffirm, realAffirm);
    mirrorAffirm(selAffirm, realAffirm);
  }

  // Copy the live Affirm line into one of our mirrors, so the monthly figure
  // tracks whatever the live line says.
  function mirrorAffirm(target, src) {
    if (!target) return;
    target.innerHTML = src.innerHTML;
    target.style.display = '';
  }
  // find the first matching element that is NOT inside our own float block
  function firstRealMatch(sel) {
    const all = qsa(sel);
    for (let i = 0; i < all.length; i++) {
      if (!all[i].closest('.cfg2026-floatprice') && !all[i].closest('.cfg2026-root')
          && !all[i].closest('.cfg2026-selections')) return all[i];
    }
    return null;
  }

  // ===========================================================================
  // RENDER
  // ===========================================================================
  function buildShell() {
    // --- Stage overlay -------------------------------------------------------
    // A 100vh relative layer over the existing .image_section. Everything that
    // floats over the chair (price, popover) is absolute INSIDE this stage so it
    // shares one coordinate space and nothing shifts when the nav drops in.
    // Two transparent edge gutters flank the center 40% and take normal page
    // scroll; the center column sits over the Dopple canvas untouched, so drag
    // to rotate / wheel to zoom there behave exactly as before.
    stage = el('div', 'cfg2026-stage');
    const gutterL = el('div', 'cfg2026-gutter cfg2026-gutter--l');
    const gutterR = el('div', 'cfg2026-gutter cfg2026-gutter--r');
    stage.appendChild(gutterL);
    stage.appendChild(gutterR);

    // floating running price (top-left of the stage), with the Affirm line and
    // the Anthros Black upsell beneath it (populated from the live DOM)
    const priceTag = el('div', 'cfg2026-floatprice');
    const s = sale();

    const priceRow = el('div', 'cfg2026-pricerow');
    const pv = el('div', 'cfg2026-price cfg2026-price--float');
    priceRow.appendChild(pv);
    // strike = this exact build at normal option pricing (filled by updatePrice)
    if (s.active) priceRow.appendChild(el('s', 'cfg2026-price-was'));
    priceTag.appendChild(priceRow);
    if (s.active) priceTag.appendChild(buildSaleRow());
    floatAffirm = el('p', 'affirm-line');
    floatAffirm.style.display = 'none';
    priceTag.appendChild(floatAffirm);
    floatBlack = el('a', 'anthros-blk-lnk');
    floatBlack.href = '/products/anthros-black';
    floatBlack.innerHTML = 'NEED IT FAST? GET ANTHROS BLACK FOR $1,799 <span>\u203A</span>';
    priceTag.appendChild(floatBlack);
    stage.appendChild(priceTag);

    // info popover floats over the chair (absolute, pointer-events:none on the
    // box, but the X opts back in so it can be dismissed)
    infoPop = el('div', 'cfg2026-infopop');
    infoPop.style.display = 'none';
    infoPopX = el('button', 'cfg2026-infopop-x', '\u00D7');
    infoPopX.setAttribute('aria-label', 'Close');
    infoPopX.addEventListener('click', function (e) { e.stopPropagation(); hideInfoPop(); });
    infoPop.appendChild(infoPopX);
    infoPopBody = el('div', 'cfg2026-infopop-body');
    infoPop.appendChild(infoPopBody);
    stage.appendChild(infoPop);

    const host = qs('.image_section') || document.body;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(stage);

    root = el('div', 'cfg2026-root');
    root.innerHTML = '';

    // collapse handle
    const handle = el('button', 'cfg2026-handle');
    handle.setAttribute('aria-label', 'Show your selections');
    handle.innerHTML = '<span class="cfg2026-chev cfg2026-chev--up"></span>';   // up = pull selections open
    handle.addEventListener('click', function (e) {
      e.stopPropagation();
      toggleSelections();
    });

    // progress bar (fills as the user advances through the 10 steps)
    const progress = el('div', 'cfg2026-progress');
    progressFill = el('div', 'cfg2026-progress-fill');
    progress.appendChild(progressFill);

    root.appendChild(handle);
    root.appendChild(progress);

    const panel = el('div', 'cfg2026-panel');

    // header: prev | count+title+info | next
    const head = el('div', 'cfg2026-head');
    const prev = el('button', 'cfg2026-nav cfg2026-prev', '\u2039');
    prev.addEventListener('click', function () { go(current - 1); });
    const center = el('div', 'cfg2026-headcenter');
    countEl = el('div', 'cfg2026-count', '1/' + TOTAL_STEPS);
    const titleWrap = el('div', 'cfg2026-titlewrap');
    titleEl = el('div', 'cfg2026-title', '');
    infoBtn = el('button', 'cfg2026-info', '\u24D8');
    infoBtn.style.display = 'none';
    infoBtn.addEventListener('click', toggleInfo);
    titleWrap.appendChild(titleEl);
    titleWrap.appendChild(infoBtn);
    center.appendChild(countEl);
    center.appendChild(titleWrap);
    const next = el('button', 'cfg2026-nav cfg2026-next', '\u203A');
    next.addEventListener('click', function () { go(current + 1); });
    head.appendChild(prev);
    head.appendChild(center);
    head.appendChild(next);
    panel.appendChild(head);

    panelBody = el('div', 'cfg2026-body');
    panel.appendChild(panelBody);

    root.appendChild(panel);
    // The bar lives inside the stage (absolute to it), not body, so it shares
    // the same 100vh coordinate space as the price and popover. Nothing shifts
    // when the nav drops in.
    (stage || document.body).appendChild(root);
    root._nav = { prev: prev, next: next };

    // Promo sticker: sits above the bar (and above the handle) over the chair.
    // A child of the bar so it tracks the bar's position on every breakpoint.
    if (s.active && s.bannerDesktop) {
      saleBanner = el('img', 'cfg2026-salebanner');
      saleBanner.src = (isMobile() && s.bannerMobile) ? s.bannerMobile : s.bannerDesktop;
      saleBanner.alt = s.bannerAlt || '';
      root.appendChild(saleBanner);
    }

    buildSelectionsCard();
  }

  // --- Design Selections card (toggled by the bar handle) --------------------
  // A floating checklist of the current selections + add-to-cart, sliding up
  // over the chair. Auto-opens on the final step; user can close it to play
  // with the model, or reopen via the handle.
  function buildSelectionsCard() {
    selCard = el('div', 'cfg2026-selections');

    const closeChev = el('button', 'cfg2026-sel-close');
    closeChev.setAttribute('aria-label', 'Hide your selections');
    closeChev.innerHTML = '<span class="cfg2026-chev cfg2026-chev--down"></span>';   // down = push closed
    closeChev.addEventListener('click', function (e) { e.stopPropagation(); toggleSelections(false); });
    selCard.appendChild(closeChev);

    const head = el('div', 'cfg2026-sel-head');
    head.appendChild(el('span', 'cfg2026-sel-title', 'DESIGN SELECTIONS'));
    const headRight = el('div', 'cfg2026-sel-headright');
    selCount = el('span', 'cfg2026-sel-count', String(STEPS.length));
    headRight.appendChild(selCount);
    const xBtn = el('button', 'cfg2026-sel-x', '\u00D7');
    xBtn.setAttribute('aria-label', 'Close');
    xBtn.addEventListener('click', function (e) { e.stopPropagation(); toggleSelections(false); });
    headRight.appendChild(xBtn);
    head.appendChild(headRight);
    selCard.appendChild(head);

    selList = el('div', 'cfg2026-sel-list');
    selCard.appendChild(selList);

    // Footer: total + Affirm above the button, reassurance below it. Built on
    // every breakpoint but only shown on mobile (CSS) -- desktop has the bar.
    const foot = el('div', 'cfg2026-sel-foot');
    const totalRow = el('div', 'cfg2026-sel-total');
    totalRow.appendChild(el('span', 'cfg2026-sel-totallabel', 'TOTAL:'));
    totalRow.appendChild(el('span', 'cfg2026-price cfg2026-sel-totalval'));
    if (sale().active) totalRow.appendChild(el('s', 'cfg2026-price-was'));
    foot.appendChild(totalRow);
    selAffirm = el('p', 'affirm-line cfg2026-sel-affirm');
    selAffirm.style.display = 'none';
    foot.appendChild(selAffirm);
    selCard.appendChild(foot);

    const cta = el('button', 'cfg2026-sel-cta', '');
    cta.innerHTML = CART_SVG + '<span class="add_to_cart_text">ADD TO CART</span>';
    cta.addEventListener('click', triggerAddToCart);
    selCard.appendChild(cta);

    const trust = el('div', 'cfg2026-sel-trust');
    trust.innerHTML = '<span>\u2713 60 Day Risk Free Trial</span><span>\u2713 12 Year Warranty</span>';
    selCard.appendChild(trust);

    const ship = el('div', 'cfg2026-sel-ship');
    ship.innerHTML = '<img src="https://cdn.shopify.com/s/files/1/0709/8423/7358/files/box-icon.svg?v=1714128832" alt="" />'
      + '<span>Custom built &amp; shipped in 4 - 7 business days</span>';
    selCard.appendChild(ship);

    // HSA/FSA note mirrored from the live DOM (keeps its Learn More link)
    const hsaSrc = qs('.hsa-text');
    if (hsaSrc && (hsaSrc.textContent || '').trim()) {
      const hsa = el('div', 'cfg2026-sel-hsa');
      hsa.innerHTML = hsaSrc.innerHTML;
      selCard.appendChild(hsa);
    }

    (stage || document.body).appendChild(selCard);
  }

  // --- Sale row: SAVE UP TO chip + label + countdown -------------------------
  function buildSaleRow() {
    const s = sale();
    const row = el('div', 'cfg2026-salerow');
    saveChip = el('span', 'cfg2026-savechip', 'SAVE UP TO ' + money(s.maxSavings || 0));
    row.appendChild(saveChip);
    const info = el('div', 'cfg2026-saleinfo');
    info.appendChild(el('span', 'cfg2026-salelabel', s.label || ''));
    countdownEl = el('span', 'cfg2026-countdown', '');
    info.appendChild(countdownEl);
    row.appendChild(info);
    startCountdown(row);
    return row;
  }

  function startCountdown(row) {
    const end = new Date(sale().deadline).getTime();
    if (!countdownEl || !end) return;
    const pad = function (n) { return (n < 10 ? '0' : '') + n; };
    let t;   // declared first: the very first tick may already be past deadline
    const tick = function () {
      const d = end - Date.now();
      if (d <= 0) {                       // expired: pull the promo furniture
        row.style.display = 'none';
        if (saleBanner) saleBanner.style.display = 'none';
        document.body.classList.remove('cfg2026-sale');
        qsa('.cfg2026-price-was').forEach(function (n) { n.style.display = 'none'; });
        clearInterval(t);
        return;
      }
      countdownEl.textContent = 'Expires in '
        + pad(Math.floor(d / 864e5)) + ':'
        + pad(Math.floor(d % 864e5 / 36e5)) + ':'
        + pad(Math.floor(d % 36e5 / 6e4)) + ':'
        + pad(Math.floor(d % 6e4 / 1e3));
    };
    tick();
    t = setInterval(tick, 1000);
  }

  function toggleSelections(force) {
    selectionsOpen = (typeof force === 'boolean') ? force : !selectionsOpen;
    if (selectionsOpen) renderSelections();
    if (root) root.classList.toggle('cfg2026-sel-open', selectionsOpen);
    // also on body: the float price is a sibling BEFORE the bar, so a CSS
    // sibling selector can't reach it from root
    document.body.classList.toggle('cfg2026-sel-open', selectionsOpen);
    if (selCard) selCard.classList.toggle('is-open', selectionsOpen);
  }

  function renderSelections() {
    if (!selList) return;
    selList.innerHTML = '';
    let n = 0;
    STEPS.forEach(function (step) {
      if (isStepHidden(step)) return;
      const sel = currentSelectionFor(step);
      const isVisited = !!visited[step.key];
      const row = el('button', 'cfg2026-sel-row');

      // left bubble: show the chosen swatch if we have one, else a plain dot
      const dot = el('span', 'cfg2026-sel-dot');
      if (sel && sel.img) {
        dot.classList.add('has-img');
        const img = el('img');
        img.src = sel.img; img.alt = sel.label; img.loading = 'lazy';
        dot.appendChild(img);
      }
      row.appendChild(dot);

      const txt = el('span', 'cfg2026-sel-txt');
      txt.appendChild(el('span', 'cfg2026-sel-label', step.label));
      const v = el('span', 'cfg2026-sel-val');
      if (sel) {
        v.textContent = displayName(sel.label);
        const orig = saleOriginal(sel.id);
        if (orig) {
          v.appendChild(el('s', 'cfg2026-sel-was', '+$' + orig));
          v.appendChild(el('span', 'cfg2026-sel-free', '+$0'));
        } else {
          const chip = priceChip(sel.price);
          if (chip) v.appendChild(document.createTextNode('  ' + chip));
        }
      } else {
        v.textContent = '\u2014';
      }
      txt.appendChild(v);
      row.appendChild(txt);

      // check only once the user has actually made a choice on this step
      const check = el('span', 'cfg2026-sel-check', '\u2713');
      check.style.visibility = isVisited ? 'visible' : 'hidden';
      row.appendChild(check);

      (function (idx) { row.addEventListener('click', function () { toggleSelections(false); go(idx); }); })(STEPS.indexOf(step));
      selList.appendChild(row);
      if (isVisited) n++;
    });
    if (selCount) selCount.textContent = String(n);
    updatePrice();   // card total + strike are only in the DOM once it's built
  }

  function toggleInfo(e) {
    if (e) e.stopPropagation();
    const showing = infoPop.style.display !== 'none';
    if (showing) { hideInfoPop(); }
    else {
      infoPop.style.display = 'block';
      // opening is a user gesture (the "i" click), so we're allowed to play with
      // sound. The autoplay attribute wouldn't count as a gesture; play() here does.
      const v = infoPop.querySelector('.cfg2026-infovid');
      if (v) { v.muted = false; v.currentTime = 0; const p = v.play(); if (p && p.catch) p.catch(function(){}); }
    }
  }
  function hideInfoPop() {
    if (!infoPop) return;
    infoPop.style.display = 'none';
    const v = infoPop.querySelector('.cfg2026-infovid');
    if (v) { v.pause(); }   // stop audio when the popover closes
  }
  function closeInfo(e) {
    if (e && infoBtn && (e.target === infoBtn || (e.target.closest && e.target.closest('.cfg2026-info')))) return;
    hideInfoPop();
  }
  // Any click anywhere (except the i button) dismisses the popover.
  document.addEventListener('click', closeInfo, true);

  function setInfo(step) {
    let txt = null;
    let media = '';
    // per-option info (Base, Arms): the popover describes the CURRENTLY selected
    // option. The trigger is an inline "i" rendered under that option (see
    // renderSwatchRow), so the header "i" stays hidden on these steps.
    if (step.infoByOption) {
      const sel = currentSelectionFor(step);
      txt = sel ? step.infoByOption(sel.label) : null;
      if (sel && step.mediaByOption) media = step.mediaByOption(sel.label) || '';
      infoBtn.style.display = 'none';
    } else {
      txt = step.info;
      if (typeof txt === 'function') txt = txt(activeTab[step.key]);
      // step-level video: one clip for the whole step (e.g. Foot Support), shown
      // with the header "i" popover. Resolve a function or a plain string.
      if (step.media) media = (typeof step.media === 'function' ? step.media(activeTab[step.key]) : step.media) || '';
      infoBtn.style.display = txt ? '' : 'none';
    }
    if (txt) {
      let html = '';
      if (media) {
        // video at the top of the popover, copy below
        html += '<video class="cfg2026-infovid" src="' + media + '" loop playsinline preload="auto"></video>';
      }
      html += infoText(txt);
      infoPopBody.innerHTML = html;
    }
    infoPop.style.display = 'none';
  }
  // turn a plain info string (with \n) into safe HTML with line breaks. A blank
  // line separates a bold heading (first block) from body copy.
  function infoText(txt) {
    const esc = function (s) {
      return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    };
    const blocks = txt.split('\n\n');
    let html = '';
    blocks.forEach(function (b, i) {
      const lines = b.split('\n').map(esc).join('<br>');
      if (i === 0 && blocks.length > 1) html += '<strong class="cfg2026-infohead">' + lines + '</strong>';
      else html += '<span class="cfg2026-infobody">' + lines + '</span>';
    });
    return html;
  }

  function updateProgress() {
    if (!progressFill) return;
    const pct = Math.round(((current + 1) / TOTAL_STEPS) * 100);
    progressFill.style.width = pct + '%';
  }

  // ---- Auto-rotation --------------------------------------------------------
  // The chair auto-rotates on load as an attract loop. The moment the user
  // engages (advances past Chair Color, or changes any option) we stop it and
  // never restart. Guarded so it only ever fires once no matter how many call
  // sites reach it.
  let autoRotateStopped = false;
  function stopAutoRotate() {
    if (autoRotateStopped) return;
    if (typeof window.setAutoRotation === 'function') {
      window.setAutoRotation(false);
      autoRotateStopped = true;
    } else if (window.dopple && typeof window.dopple.updateSelection === 'function') {
      window.dopple.updateSelection({ autoRotation: 'off' });
      autoRotateStopped = true;
    }
  }

  // ---- Camera views ---------------------------------------------------------
  // Driven by step ENTRY, not by option clicks. Binding setCameraView to
  // individual radios (the old approach) meant the camera only moved if the user
  // actually picked something -- arrowing into a step left the chair wherever it
  // was. This is called from go() only, so re-renders from afterChange don't
  // yank the camera back while someone is rotating the model.
  function applyCamera(view) {
    if (!view) return;
    if (typeof window.setCameraView === 'function') {
      window.setCameraView(view);
      return true;
    }
    if (window.dopple && typeof window.dopple.updateSelection === 'function') {
      window.dopple.updateSelection({ cameraView: view });
      return true;
    }
    return false;
  }
  function setStepCamera(step) {
    if (!step || !step.camera) return;
    // Dopple may not be ready on the very first step; retry briefly.
    if (applyCamera(step.camera)) return;
    let tries = 0;
    const t = setInterval(function () {
      if (applyCamera(step.camera) || ++tries > 20) clearInterval(t);
    }, 150);
  }

  function go(idx) {
    if (idx < 0) idx = 0;
    if (idx > REVIEW_INDEX) idx = REVIEW_INDEX;
    // skip steps APO has collapsed to a single choice, in the direction of travel
    const dir = idx >= current ? 1 : -1;
    while (idx > 0 && idx < REVIEW_INDEX && isStepHidden(STEPS[idx])) idx += dir;
    current = idx;
    if (current > 0) stopAutoRotate();   // advanced past Chair Color: stop the attract loop
    updateProgress();
    if (current === REVIEW_INDEX) renderReview();
    else { renderStep(); setStepCamera(STEPS[current]); }
  }

  function renderStep() {
    const step = STEPS[current];
    visited[step.key] = true;   // reaching a step counts as accepting its selection (default or chosen)
    // CSS hook: lets rules target a single step, e.g. [data-step="base"] .cfg2026-info
    root.setAttribute('data-step', step.key);
    countEl.textContent = (current + 1) + '/' + TOTAL_STEPS;
    titleEl.textContent = step.label.toUpperCase();
    root._nav.prev.style.visibility = current === 0 ? 'hidden' : 'visible';
    root._nav.next.style.visibility = 'visible';
    setInfo(step);

    ensureValidSelection(step);

    panelBody.innerHTML = '';
    const ctrl = getControl(step);

    if (ctrl.kind === 'select') {
      renderSelectStep(step, ctrl.sel);
      return;
    }
    if (ctrl.kind !== 'radio') {
      panelBody.appendChild(el('div', 'cfg2026-empty', 'Loading options...'));
      return;
    }

    let opts = radioOptions(step, ctrl.sec);

    // static subhead ("Choose your X from the options below.") above the options
    if (step.subhead) panelBody.appendChild(el('div', 'cfg2026-subhead', step.subhead));

    // sub-type tabs (Back, Fabric)
    if (step.tabs) {
      ensureActiveTab(step, opts);
      panelBody.appendChild(buildTabRow(step, function () { setInfo(step); renderStep(); }));
      opts = opts.filter(function (o) { return o.subtype === activeTab[step.key]; });
    }

    panelBody.appendChild(renderSwatchRow(opts, step));
  }

  // split a name into two balanced lines (one word per line for 2-word names)
  function autoStack(label) {
    const words = String(label).trim().split(/\s+/);
    if (words.length < 2) return null;
    if (words.length === 2) return [words[0], words[1]];
    const mid = Math.ceil(words.length / 2);
    return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
  }
  // display-only relabels + drop a trailing "PORT". Native MageWorx labels stay
  // as-is ("IVORY PORT", "BITCOIN MONEY", "US FLAG"), so 3D matching via
  // configMenu is untouched.
  const DISPLAY_RENAME = {
    'US FLAG': 'USA FLAG',
    'BITCOIN MONEY': 'BITCOIN CREAM',
    'BITCOIN LETTERPRESS': 'BITCOIN BLACK'
  };
  function displayName(label) {
    const out = String(label).replace(/\s+port\s*$/i, '').trim();
    return DISPLAY_RENAME[out.toUpperCase()] || out;
  }
  function buildOptLabel(step, label, allowStack) {
    const wrap = el('span', 'cfg2026-optlabel');
    let stack = step && step.labelStack ? step.labelStack(label) : null;
    if (!stack && allowStack) stack = autoStack(label);
    if (stack) {
      wrap.classList.add('cfg2026-optlabel--stack');
      wrap.appendChild(el('span', 'cfg2026-optlabel-1', stack[0]));
      wrap.appendChild(el('span', 'cfg2026-optlabel-2', stack[1]));
    } else {
      wrap.textContent = label;
    }
    return wrap;
  }

  function renderSwatchRow(opts, step) {
    const row = el('div', 'cfg2026-options');
    // specialty backs (10 swatches) lay out as an even 5-and-5 grid on desktop
    if (step && step.key === 'back' && opts.length && opts[0].subtype === 'specialty') {
      row.classList.add('cfg2026-options--grid5');
    }
    opts.forEach(function (o) {
      const hasImg = !!o.img;
      const btn = el('button', 'cfg2026-opt' + (o.checked ? ' is-selected' : '') + (o.oos ? ' is-oos' : '') + (hasImg ? '' : ' cfg2026-opt--text'));
      if (hasImg) {
        const sw = el('span', 'cfg2026-swatch');
        const img = el('img');
        img.src = o.img; img.alt = o.label; img.loading = 'lazy';
        sw.appendChild(img);
        btn.appendChild(sw);
      }
      btn.appendChild(buildOptLabel(step, displayName(o.label), hasImg));
      appendChip(btn, o);
      if (o.oos) btn.appendChild(el('span', 'cfg2026-oos', 'Out of stock'));
      // per-option info steps (Base, Arms): the "i" sits under the SELECTED
      // option only, so it always reads as belonging to that option. A span (not
      // a button) since it lives inside the option button.
      if (step && step.infoByOption && o.checked && step.infoByOption(o.label)) {
        if (MODE === 'mobile') {
          const ii = el('span', 'cfg2026-info cfg2026-info--inline', '\u24D8');
          ii.setAttribute('role', 'button');
          ii.setAttribute('tabindex', '0');
          ii.setAttribute('aria-label', 'More info');
          ii.addEventListener('click', function (e) { e.stopPropagation(); toggleInfo(e); });
          btn.appendChild(ii);
        } else {
          btn.appendChild(makeInfoIcon(step.infoByOption(o.label), step.mediaByOption ? step.mediaByOption(o.label) : ''));
        }
      }
      btn.addEventListener('click', function () { visited[step.key] = true; chooseRadio(o); });
      row.appendChild(btn);
    });
    if (!opts.length) row.appendChild(el('div', 'cfg2026-empty', 'No options available.'));
    return row;
  }

  function buildSelectRow(step, sel) {
    const opts = selectOptions(sel);
    const row = el('div', 'cfg2026-options cfg2026-options--list');
    opts.forEach(function (o) {
      const btn = el('button', 'cfg2026-opt cfg2026-opt--text' + (o.selected ? ' is-selected' : ''));
      btn.appendChild(buildOptLabel(step, o.label));
      const chip = priceChip(o.price);
      if (chip) btn.appendChild(el('span', 'cfg2026-chip', chip));
      btn.addEventListener('click', function () { visited[step.key] = true; chooseSelect(sel, o.value); });
      row.appendChild(btn);
    });
    if (!opts.length) row.appendChild(el('div', 'cfg2026-empty', 'No options available.'));
    return row;
  }
  function renderSelectStep(step, sel) {
    if (step.subhead) panelBody.appendChild(el('div', 'cfg2026-subhead', step.subhead));
    panelBody.appendChild(buildSelectRow(step, sel));
  }

  // ===========================================================================
  // DESKTOP (>=1025): same render kit, stacked cards, no stepper / drawer.
  //   html.cfg-control   -> Back Panel & Fabric collapsed behind sub-group tabs
  //   html.cfg-treatment -> Back Panel & Fabric fully expanded (all sub-groups)
  // Native APO options are moved off-screen (still readable) like mobile; the
  // desktop Add-to-Cart / price block is left in place.
  // ===========================================================================
  function isControlVariant() {
    return document.documentElement.classList.contains('cfg-control');
  }
  function ensureActiveTab(step, opts) {
    if (!activeTab[step.key]) {
      const checked = opts.filter(function (o) { return o.checked; })[0];
      activeTab[step.key] = (checked && checked.subtype) ? checked.subtype : step.tabs[0].id;
    }
  }
  function buildTabRow(step, onSelect) {
    const tabRow = el('div', 'cfg2026-tabs');
    step.tabs.forEach(function (t) {
      const b = el('button', 'cfg2026-tab' + (activeTab[step.key] === t.id ? ' is-active' : ''));
      t.label.split('\n').forEach(function (line, i) {
        if (i > 0) b.appendChild(document.createElement('br'));
        b.appendChild(document.createTextNode(line));
      });
      b.addEventListener('click', function () { activeTab[step.key] = t.id; onSelect(); });
      tabRow.appendChild(b);
    });
    return tabRow;
  }
  // desktop info: a single floating popover (mirrors the mobile infoPop UX).
  // Each inline "i" fills it with that item's text/media and shows it; it
  // dismisses on the X or an outside click.
  function buildDeskPopover() {
    if (deskPop) return;
    deskPop = el('div', 'cfg2026-deskpop');
    deskPop.style.display = 'none';
    const x = el('button', 'cfg2026-deskpop-x', '\u00D7');
    x.type = 'button'; x.setAttribute('aria-label', 'Close');
    x.addEventListener('click', function (e) { e.stopPropagation(); closeDeskPop(); });
    deskPop.appendChild(x);
    deskPopBody = el('div', 'cfg2026-deskpop-body');
    deskPop.appendChild(deskPopBody);
    document.body.appendChild(deskPop);
    // outside-click dismiss (capture phase; ignore clicks on an "i" or inside the pop)
    document.addEventListener('click', function (e) {
      if (!deskPop || deskPop.style.display === 'none') return;
      if (e.target.closest && (e.target.closest('.cfg2026-deskpop') || e.target.closest('.cfg2026-info'))) return;
      closeDeskPop();
    }, true);
  }
  function openDeskPop(text, media) {
    if (!deskPop) buildDeskPopover();
    deskPopBody.innerHTML = '';
    if (media) {
      const v = el('video', 'cfg2026-infovid');
      v.src = media; v.loop = true; v.setAttribute('playsinline', ''); v.preload = 'auto';
      deskPopBody.appendChild(v);
      const pl = v.play(); if (pl && pl.catch) pl.catch(function () {});
    }
    if (text) String(text).split('\n').forEach(function (line) {
      if (line.trim()) deskPopBody.appendChild(el('p', 'cfg2026-deskpop-p', line));
    });
    deskPop.style.display = 'block';
  }
  function closeDeskPop() {
    if (!deskPop) return;
    deskPop.style.display = 'none';
    const v = deskPop.querySelector('video'); if (v) v.pause();
  }
  function makeInfoIcon(text, media) {
    const ii = el('span', 'cfg2026-info cfg2026-info--inline', '\u24D8');
    ii.setAttribute('role', 'button');
    ii.setAttribute('tabindex', '0');
    ii.setAttribute('aria-label', 'More info');
    ii.addEventListener('click', function (e) { e.stopPropagation(); e.preventDefault(); openDeskPop(text, media); });
    return ii;
  }
  function stepInfoText(step, tabId) {
    if (!step.info) return null;
    return (typeof step.info === 'function') ? step.info(tabId) : step.info;
  }
  function stepMedia(step, tabId) {
    if (!step.media) return '';
    return (typeof step.media === 'function' ? step.media(tabId) : step.media) || '';
  }
  function renderDesktopCard(step) {
    const card = el('div', 'cfg2026-card');
    card.setAttribute('data-step', step.key);

    const head = el('div', 'cfg2026-card-head');
    head.appendChild(el('span', 'cfg2026-card-title', step.label.toUpperCase()));
    card.appendChild(head);
    if (step.subhead) card.appendChild(el('div', 'cfg2026-subhead', step.subhead));

    const ctrl = getControl(step);
    if (ctrl.kind === 'select') { card.appendChild(buildSelectRow(step, ctrl.sel)); return card; }
    if (ctrl.kind !== 'radio') { card.appendChild(el('div', 'cfg2026-empty', 'Loading options...')); return card; }

    ensureValidSelection(step);
    const opts = radioOptions(step, ctrl.sec);
    const control = isControlVariant();
    if (step.tabs && control) ensureActiveTab(step, opts);

    // step-level info icon INLINE after the title (like mobile "BACK PANEL (i)").
    // Skipped for treatment-tabs (info shown per sub-group) and for Base/Arms
    // (per-option info handled inside the option).
    if (!step.infoByOption && !(step.tabs && !control)) {
      const ctxTab = (step.tabs && control) ? activeTab[step.key] : null;
      const t = stepInfoText(step, ctxTab), m = stepMedia(step, ctxTab);
      if (t || m) { head.appendChild(makeInfoIcon(t, m)); }
    }

    if (step.tabs && control) {
      card.appendChild(buildTabRow(step, function () { renderDesktopAll(); }));
      card.appendChild(renderSwatchRow(opts.filter(function (o) { return o.subtype === activeTab[step.key]; }), step));
    } else if (step.tabs) {
      step.tabs.forEach(function (t) {
        const sub = opts.filter(function (o) { return o.subtype === t.id; });
        if (!sub.length) return;
        const sh = el('div', 'cfg2026-subgroup', t.label.replace(/\n/g, ' '));
        const it = stepInfoText(step, t.id), im = stepMedia(step, t.id);
        card.appendChild(sh);
        if (it || im) { sh.appendChild(makeInfoIcon(it, im)); }
        card.appendChild(renderSwatchRow(sub, step));
      });
    } else {
      card.appendChild(renderSwatchRow(opts, step));
    }
    return card;
  }
  function renderDesktopAll() {
    if (!deskHost) return;
    const frag = document.createDocumentFragment();
    STEPS.forEach(function (step) { if (!isStepHidden(step)) frag.appendChild(renderDesktopCard(step)); });
    deskHost.innerHTML = '';
    deskHost.appendChild(frag);
  }
  // Price bar over the image (bottom-left) + the "need it fast" upsell link
  // (top-left), mirrored from the live DOM. Replaces the cyan price box that
  // used to sit at the top of the options column.
  function buildDeskChrome() {
    const host = qs('.image_section');
    if (!host) return;
    if (!qs('.cfg2026-deskprice', host)) {
      const bar = el('div', 'cfg2026-deskprice');
      deskPriceEl = el('div', 'cfg2026-deskprice-val');
      deskAffirmEl = el('div', 'cfg2026-deskprice-affirm');
      bar.appendChild(deskPriceEl);
      bar.appendChild(deskAffirmEl);
      host.appendChild(bar);
    }
    if (!qs('.cfg2026-desklink', host)) {
      const src = qs('.anthros-blk-lnk');
      if (src) {
        const a = el('a', 'cfg2026-desklink');
        a.href = src.getAttribute('href') || '#';
        a.innerHTML = src.innerHTML;
        host.appendChild(a);
      }
    }
    updateDeskPrice();
  }
  function updateDeskPrice() {
    if (deskPriceEl) { const p = readPrice(); if (p) deskPriceEl.textContent = p; }
    if (deskAffirmEl) {
      const src = firstRealMatch('.affirm-line');
      deskAffirmEl.textContent = src ? (src.textContent || '').replace(/\s+/g, ' ').trim() : '';
    }
  }

  function initDesktop() {
    initialized = true;
    MODE = 'desktop';
    if (sale().active) document.body.classList.add('cfg2026-sale');
    document.body.classList.add('cfg2026-desktop-active');
    buildDeskPopover();
    const apo = qs('.mw-apo-container');
    if (!apo) { initialized = false; return; }
    deskHost = el('div', 'cfg2026-desk');
    apo.parentNode.insertBefore(deskHost, apo.nextSibling);
    renderDesktopAll();
    buildDeskChrome();

    // APO may finish populating after first paint: re-mirror until options show
    let n = 0;
    const t = setInterval(function () {
      renderDesktopAll();
      updateDeskPrice();
      if (deskHost.querySelector('.cfg2026-opt')) { clearInterval(t); }
      else if (++n > 20) { clearInterval(t); document.documentElement.classList.add('cfg2026-desk-fallback'); }
    }, 400);

    // External APO changes (preset URL, Quartz cascade) -> re-mirror
    if ('MutationObserver' in window) {
      let d;
      new MutationObserver(function () {
        clearTimeout(d);
        d = setTimeout(function () { renderDesktopAll(); updateDeskPrice(); }, 200);
      }).observe(apo, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'class', 'style'] });
    }

    window.cfg2026 = { refresh: renderDesktopAll, steps: STEPS, mode: 'desktop' };
  }
  function boot() {
    if (initialized) return;
    if (!qs('.mw-apo-container')) return;
    if (isMobile()) initMobile();
    else initDesktop();
  }

  // ---- Review (step 10) -----------------------------------------------------
  function currentSelectionFor(step) {
    const ctrl = getControl(step);
    if (ctrl.kind === 'select') {
      const o = selectOptions(ctrl.sel).filter(function (x) { return x.selected; })[0];
      return o ? { label: o.label, price: o.price, img: o.img || null } : null;
    }
    if (ctrl.kind === 'radio') {
      const o = radioOptions(step, ctrl.sec).filter(function (x) { return x.checked; })[0];
      return o ? { label: o.label, price: o.price, img: o.img || null, id: o.id } : null;
    }
    return null;
  }

  function renderReview() {
    root.setAttribute('data-step', 'review');
    countEl.textContent = TOTAL_STEPS + '/' + TOTAL_STEPS;
    titleEl.textContent = 'YOUR ANTHROS CHAIR';
    infoBtn.style.display = 'none';
    infoPop.style.display = 'none';
    root._nav.prev.style.visibility = 'visible';
    root._nav.next.style.visibility = 'hidden';

    panelBody.innerHTML = '';

    // Final step is just price + CTA + trust. The full checklist lives in the
    // Design Selections card (handle), which we auto-open here.
    const totalRow = el('div', 'cfg2026-totalrow');
    totalRow.appendChild(el('span', 'cfg2026-totallabel', 'TOTAL'));
    const tot = el('span', 'cfg2026-price cfg2026-total');
    tot.textContent = readPrice();
    totalRow.appendChild(tot);
    if (sale().active) totalRow.appendChild(el('s', 'cfg2026-price-was'));
    panelBody.appendChild(totalRow);
    updatePrice();   // fills total + strike now that both are in the DOM

    // (Affirm line + Anthros Black upsell now live in the float-price block,
    // visible on every step, so they are not repeated here.)

    const cta = el('button', 'cfg2026-addtocart', '');
    cta.innerHTML = CART_SVG + '<span class="add_to_cart_text">ADD TO CART</span>';
    cta.addEventListener('click', triggerAddToCart);
    panelBody.appendChild(cta);

    const trust = el('div', 'cfg2026-trust', '60-Day Risk Free Trial  \u00B7  12 Year Warranty  \u00B7  Custom built & shipped in 4\u20137 business days');
    panelBody.appendChild(trust);

    // HSA/FSA note from the live DOM if present
    const hsaSrc = qs('.hsa-text');
    if (hsaSrc) {
      const hsa = el('div', 'cfg2026-hsa');
      hsa.textContent = (hsaSrc.textContent || '').trim();
      if (hsa.textContent) panelBody.appendChild(hsa);
    }

    toggleSelections(true);   // auto-open the selections card on the final step
  }

  // ---- Add to cart: proxy the existing cloned button (keeps snapshot pipeline)
  // The snapshot/Klaviyo/BUILDING-overlay pipeline is bound to a click on
  // `.mw-apo-add-to-cart-cloned`. That clone is created by the APO glue at
  // runtime (not in the Liquid), so we click it. If it isn't present yet we
  // run the APO validation gate (which reveals it) and retry; final fallback is
  // the native theme submit `.product-form__submit` so a cart add still happens.
  function triggerAddToCart() {
    const gate = qs('#custom_add_to_cart_btn');
    if (gate) gate.click();   // APO required-field validation; also reveals the clone

    const clone = qs('.mw-apo-add-to-cart-cloned');
    if (clone) { clone.click(); return; }

    setTimeout(function () {
      const c = qs('.mw-apo-add-to-cart-cloned');
      if (c) { c.click(); return; }
      const nativeSubmit = qs('product-form.product-form .product-form__submit');
      if (nativeSubmit) nativeSubmit.click();
    }, 450);
  }

  // ===========================================================================
  // INIT
  // ===========================================================================
  function initMobile() {
    initialized = true;
    document.body.classList.add('cfg2026-active');
    if (sale().active) document.body.classList.add('cfg2026-sale');
    buildShell();
    initNavSolid();
    initPriceObserver();
    go(0);
    updatePrice();

    // Carousel is now on screen: lift the boot gate so the page reveals the new
    // layout, never the raw APO layout underneath. rAF lets this paint settle
    // first so there's no flash of the original between hide and reveal.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.documentElement.classList.remove('cfg2026-booting');
      });
    });

    // price keeps flowing from APO; light refresh in case the first paint is empty
    let n = 0;
    const t = setInterval(function () { updatePrice(); if (++n > 10) clearInterval(t); }, 400);

    // expose a tiny API for debugging / manual control
    window.cfg2026 = {
      go: go,
      refresh: function () { if (current < REVIEW_INDEX) renderStep(); else renderReview(); },
      steps: STEPS
    };
  }

  // The mobile header stays fixed. It sits transparent over the chair and gains
  // its solid background once the stage has scrolled out from under it, so the
  // white icons never end up on the white panel. The negative top rootMargin is
  // the header's own height: the stage stops "intersecting" the moment its
  // bottom edge passes behind the header rather than the top of the viewport.
  function initNavSolid() {
    const stageEl = qs('.cfg2026-stage');
    if (!stageEl || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      document.body.classList.toggle('cfg2026-navsolid', !entries[0].isIntersecting);
    }, { rootMargin: '-90px 0px 0px 0px', threshold: 0 }).observe(stageEl);
  }

  // Safety: never leave the page gated. If something goes wrong and init never
  // runs, lift the gate after a timeout so the original layout shows rather than
  // a blank screen.
  setTimeout(function () { document.documentElement.classList.remove('cfg2026-booting'); }, 8000);

  // APO dispatches `optionsReady` once values have title_name + data-price.
  window.addEventListener('optionsReady', function () { setTimeout(boot, 300); });

  // Fallback poll in case the event already fired or is missed.
  (function waitForApo() {
    let tries = 0;
    const t = setInterval(function () {
      tries++;
      const ready = qs('.mw-apo-container .mw-option__value[data-value-id] .title_name')
        || qs('.mw-apo-container select option[data-price]');
      if (ready) { clearInterval(t); boot(); }
      else if (tries > 120) { clearInterval(t); boot(); }   // ~60s safety
    }, 500);
  })();

})();